import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '../api/api.service';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth.interceptor';
import { ReservationRecord } from '../models/reservation.model';

/**
 * Server-side cap for `per_page` on the leads/reservations list endpoint
 * (parsePagination in reservations.routes.js). Asking for more silently returns
 * fewer rows than requested, which breaks "fetched a short page, so we're done"
 * paging loops.
 */
export const LEADS_PAGE_SIZE = 100;

export interface LeadProfileRecord {
    id: string;
    userId: string | null;
    propertyId: string | null;
    propertyTitle: string | null;
    propertyStatus: string | null;
    unitNumber: string | null;
    projectId: string | null;
    projectName: string | null;
    phaseId: string | null;
    phaseName: string | null;
    createdByUserId: string | null;
    createdByName: string | null;
    createdByEmail: string | null;
    createdByPhone: string | null;
    createdByRole: string | null;
    sourceLabel: string | null;
    notes: string | null;
    status: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    holdExpiresAt: string | null;
    holdExtensionCount: number | null;
    customerName: string | null;
    email: string | null;
    phone: string | null;
    identityStatus: string | null;
    confirmedData: any | null;
    profilePictureFileId: string | null;
  idDocumentFileId: string | null;
  idDocumentName: string | null;
  idDocumentBackFileId: string | null;
  idDocumentBackName: string | null;
    reservationPdfFileId: string | null;
    zohoSign: any | null;
}

export type LeadAssignmentScope = 'all' | 'project' | 'specific';
export type LeadAssignmentAssigneeType = 'sales' | 'subpartner';

export interface LeadNoteRecord {
    id: string;
    lead_id: string;
    author_user_id: string | null;
    author_name: string | null;
    body: string;
    created_at: string | null;
}

export interface LeadAssignmentAssignee {
    id: string;
    type: LeadAssignmentAssigneeType;
    name: string;
    email: string | null;
    /** 'invited' until the member activates their account; 'active' afterwards. */
    status?: string | null;
}

export interface LeadAssignmentProject {
    id: string;
    name: string;
    leadCount: number;
}

export interface LeadAssignmentRecord {
    id: string;
    scopeType: LeadAssignmentScope;
    projectId: string | null;
    projectName: string | null;
    leadId: string | null;
    leadName: string | null;
    assigneeType: LeadAssignmentAssigneeType;
    assigneeId: string;
    assigneeName: string;
    leadCount: number;
    createdAt: string | null;
}

export interface LeadAssignmentLead {
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    status: string | null;
    createdAt: string | null;
    propertyTitle: string | null;
    unitNumber: string | null;
    projectName: string | null;
}

@Injectable({ providedIn: 'root' })
export class LeadsService {
    private api = inject(ApiService);
    private readonly PATH = '/API2/leads';

    private buildBulkImportBody(
        fileId: string,
        payload: {
            create_if_missing?: boolean;
            defaultPropertyId?: string | null;
            rowPropertyOverrides?: Record<string, string>;
        } = {}
    ): any {
        const body: any = { file_id: String(fileId || '').trim() };
        if (payload.create_if_missing !== undefined) body.create_if_missing = Boolean(payload.create_if_missing);
        if (payload.defaultPropertyId) body.default_property_id = String(payload.defaultPropertyId).trim();
        if (payload.rowPropertyOverrides && Object.keys(payload.rowPropertyOverrides).length) {
            body.row_property_overrides = payload.rowPropertyOverrides;
        }
        return body;
    }

    recordLead(propertyId: string, channel?: 'whatsapp' | 'email'): Observable<void> {
        const id = String(propertyId || '').trim();
        if (!id) return of(void 0);
        const body: any = { property_id: id };
        if (channel === 'whatsapp' || channel === 'email') body.channel = channel;
        return this.api.post<any>('/API2/leads', body).pipe(
            map(() => void 0),
            catchError(() => of(void 0))
        );
    }

    /**
     * Guest (NOT signed-in) "Send Message": records the visitor as a lead and
     * returns the listing owner's contact details so the caller can open
     * WhatsApp or an email composer.
     *
     * SKIP_AUTH_REFRESH is set because a guest has no session — without it a 4xx
     * would send the auth interceptor down its refresh/clear-state path.
     */
    submitGuestContactLead(payload: {
        propertyId: string;
        fullName?: string | null;
        email?: string | null;
        phone: string;
        channel: 'whatsapp' | 'email';
    }): Observable<{ leadId: string | null; vendorEmail: string | null; vendorPhone: string | null }> {
        // Only the phone is required. Blank name/email are OMITTED rather than
        // sent as "" — the API rejects a malformed email, and an empty string
        // would read as a malformed one.
        const fullName = String(payload.fullName || '').trim();
        const email = String(payload.email || '').trim();
        const body: Record<string, string> = {
            property_id: String(payload.propertyId || '').trim(),
            phone: String(payload.phone || '').trim(),
            channel: payload.channel
        };
        if (fullName) body['full_name'] = fullName;
        if (email) body['email'] = email;
        return this.api
            .post<any>('/API2/guest-leads/contact', body, {
                context: new HttpContext().set(SKIP_AUTH_REFRESH, true)
            })
            .pipe(
                map((resp) => ({
                    leadId: resp?.lead_id ?? null,
                    vendorEmail: resp?.vendorEmail ?? null,
                    vendorPhone: resp?.vendorPhone ?? null
                }))
            );
    }

    /**
     * Guest (NOT signed-in) "Request Offer": records the visitor as a lead and
     * creates a `requested` offer for the listing team to review and send.
     *
     * `paymentPlanId` is the plan the visitor picked in the selector. It is
     * omitted when the unit has no plans, in which case the API resolves the
     * unit's default itself.
     */
    submitGuestOfferRequest(payload: {
        propertyId: string;
        email: string;
        paymentPlanId?: string | null;
    }): Observable<{ leadId: string | null; offerId: string | null; offerStatus: string | null; approvalPending: boolean }> {
        const planId = String(payload.paymentPlanId || '').trim();
        const body: Record<string, string> = {
            property_id: String(payload.propertyId || '').trim(),
            email: String(payload.email || '').trim()
        };
        if (planId) body['payment_plan_id'] = planId;
        return this.api
            .post<any>('/API2/guest-leads/request-offer', body, {
                context: new HttpContext().set(SKIP_AUTH_REFRESH, true)
            })
            .pipe(
                map((resp) => ({
                    leadId: resp?.lead_id ?? null,
                    offerId: resp?.offer_id ?? null,
                    offerStatus: resp?.offer_status ?? null,
                    approvalPending: Boolean(resp?.approval_pending)
                }))
            );
    }

    createManualLead(payload: {
        propertyId?: string;
        fullName?: string;
        email?: string;
        phone?: string;
        status?: string;
        notes?: string;
        password?: string;
        verifiedChannel?: 'phone' | 'email' | null;
        verifiedTarget?: string | null;
        verifiedEmail?: string | null;
        verifiedPhone?: string | null;
        fastReservationSessionId?: string | null;
        paymentPlanId?: string | null;
    }): Observable<{
        leadId: string;
        created: boolean | null;
        propertyId: string | null;
        userId: string | null;
        status: string | null;
        fastReservationSessionId: string | null;
    }> {
        // property_id is OPTIONAL since migration 150 — an empty id means
        // "general inquiry, no unit yet", not "invalid request".
        const id = String(payload?.propertyId || '').trim();
        const body: any = {
            full_name: payload?.fullName?.trim() || undefined,
            email: payload?.email?.trim() || undefined,
            phone: payload?.phone?.trim() || undefined,
            status: payload?.status?.trim() || undefined,
            password: payload?.password?.trim() || undefined,
            verified_channel: payload?.verifiedChannel?.trim() || undefined,
            verified_target: payload?.verifiedTarget?.trim() || undefined,
            verified_email: payload?.verifiedEmail?.trim() || undefined,
            verified_phone: payload?.verifiedPhone?.trim() || undefined,
            fast_reservation_session_id: payload?.fastReservationSessionId?.trim() || undefined,
            payment_plan_id: payload?.paymentPlanId?.trim() || undefined
        };
        if (id) body['property_id'] = id;
        const notes = String(payload?.notes || '').trim();
        if (notes) body['notes'] = notes.slice(0, 2000);
        return this.api.post<any>('/API2/leads/manual', body, { context: new HttpContext().set(SKIP_AUTH_REFRESH, true) }).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => {
                const leadId = String(data?.ROWID ?? data?.id ?? '').trim();
                if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(leadId)) {
                    throw new Error('The server did not confirm a saved lead. Please check before retrying.');
                }
                return {
                    leadId,
                    created: typeof data?.CREATED === 'boolean' ? data.CREATED : typeof data?.created === 'boolean' ? data.created : null,
                    propertyId: data?.PROPERTY_ID ?? data?.property_id ?? null,
                    userId: data?.USER_ID ?? data?.user_id ?? null,
                    status: data?.STATUS ?? data?.status ?? null,
                    fastReservationSessionId: data?.FAST_RESERVATION_SESSION_ID ?? data?.fast_reservation_session_id ?? null
                };
            })
        );
    }

    bulkImportPreview(
        fileId: string,
        payload: {
            create_if_missing?: boolean;
            defaultPropertyId?: string | null;
            rowPropertyOverrides?: Record<string, string>;
        } = {}
    ): Observable<{
        ok: boolean;
        total_rows: number;
        will_create: number;
        will_update: number;
        rows: any[];
        errors: any[];
        warnings: any[];
    }> {
        return this.api.post<any>('/API2/leads/bulk-import/preview', this.buildBulkImportBody(fileId, payload));
    }

    bulkImportApply(
        fileId: string,
        payload: {
            create_if_missing?: boolean;
            defaultPropertyId?: string | null;
            rowPropertyOverrides?: Record<string, string>;
        } = {}
    ): Observable<{
        ok: boolean;
        total_rows: number;
        created_count: number;
        updated_count: number;
        rows: any[];
        errors: any[];
        warnings: any[];
    }> {
        return this.api.post<any>('/API2/leads/bulk-import/apply', this.buildBulkImportBody(fileId, payload));
    }

    updateLeadStatus(leadId: string, status: string): Observable<void> {
        const id = String(leadId || '').trim();
        if (!id) return of(void 0);
        const body: any = { status: String(status || '').trim() };
        return this.api.patch<any>(`/API2/leads/${encodeURIComponent(id)}`, body).pipe(map(() => void 0));
    }

    listLeadNotes(leadId: string): Observable<LeadNoteRecord[]> {
        const id = String(leadId || '').trim();
        if (!id) return of([]);
        return this.api
            .get<any>(`/API2/leads/${encodeURIComponent(id)}/notes`)
            .pipe(map((resp) => (Array.isArray(resp?.data) ? resp.data : []).map((row: any) => this.mapLeadNote(row))));
    }

    addLeadNote(leadId: string, body: string, noteId: string = crypto.randomUUID()): Observable<LeadNoteRecord> {
        const id = String(leadId || '').trim();
        const text = String(body || '').trim().slice(0, 2000);
        if (!id || !text) return throwError(() => new Error('Lead and note body are required.'));
        return this.api
            .post<any>(`/API2/leads/${encodeURIComponent(id)}/notes`, { body: text, note_id: noteId })
            .pipe(map((resp) => {
                const note = this.mapLeadNote(resp?.data);
                if (note.id !== noteId || note.lead_id !== id || note.body !== text) {
                    throw new Error('The server did not confirm the saved note. Please retry.');
                }
                return note;
            }));
    }

    private mapLeadNote(row: any): LeadNoteRecord {
        return {
            id: String(row?.id ?? ''),
            lead_id: String(row?.lead_id ?? ''),
            author_user_id: row?.author_user_id ? String(row.author_user_id) : null,
            author_name: row?.author_name ? String(row.author_name) : null,
            body: String(row?.body ?? ''),
            created_at: row?.created_at ? String(row.created_at) : null
        };
    }

    updateLead(
        leadId: string,
        payload: {
            propertyId?: string;
            fullName?: string;
            email?: string;
            phone?: string;
            status?: string;
            notes?: string;
        }
    ): Observable<void> {
        const id = String(leadId || '').trim();
        if (!id) return of(void 0);
        const body: any = {};
        // An EMPTY property_id intentionally CLEARS the unit (migration 150).
        if (payload?.propertyId !== undefined) body.property_id = String(payload.propertyId || '').trim();
        if (payload?.fullName !== undefined) body.full_name = String(payload.fullName || '').trim();
        if (payload?.email !== undefined) body.email = String(payload.email || '').trim();
        if (payload?.phone !== undefined) body.phone = String(payload.phone || '').trim();
        if (payload?.status !== undefined) body.status = String(payload.status || '').trim();
        if (payload?.notes !== undefined) {
            const notes = String(payload.notes || '').trim();
            body.notes = notes ? notes.slice(0, 2000) : null;
        }
        return this.api.patch<any>(`/API2/leads/${encodeURIComponent(id)}`, body).pipe(map(() => void 0));
    }

    deleteLead(leadId: string): Observable<void> {
        const id = String(leadId || '').trim();
        if (!id) return of(void 0);
        return this.api.delete<void>(`${this.PATH}/${encodeURIComponent(id)}`);
    }

    bulkDeleteLeads(leadIds: string[]): Observable<number> {
        const ids = Array.isArray(leadIds) ? leadIds.map((value) => String(value || '').trim()).filter(Boolean) : [];
        if (!ids.length) return of(0);
        return this.api.post<any>('/API2/leads/bulk-delete', { lead_ids: ids }).pipe(
            map((resp) => resp?.deleted_count ?? resp?.data?.deleted_count ?? 0),
            map((count) => {
                const safe = Number(count);
                return Number.isFinite(safe) && safe >= 0 ? safe : 0;
            })
        );
    }

    getLeadAssignmentOptions(): Observable<{ assignees: LeadAssignmentAssignee[]; projects: LeadAssignmentProject[] }> {
        return this.api.get<any>('/API2/lead-assignments/options').pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                assignees: (Array.isArray(data?.assignees) ? data.assignees : [])
                    .map((row: any) => ({
                        id: String(row?.id || ''),
                        type: String(row?.type || '') === 'subpartner' ? 'subpartner' : 'sales',
                        name: String(row?.name || '').trim() || 'Unnamed user',
                        email: row?.email ? String(row.email) : null,
                        status: row?.status ? String(row.status) : null
                    }))
                    .filter((row: LeadAssignmentAssignee) => Boolean(row.id)),
                projects: (Array.isArray(data?.projects) ? data.projects : [])
                    .map((row: any) => ({
                        id: String(row?.id || ''),
                        name: String(row?.name || '').trim() || 'Unnamed project',
                        leadCount: Number(row?.lead_count ?? row?.leadCount ?? 0) || 0
                    }))
                    .filter((row: LeadAssignmentProject) => Boolean(row.id))
            }))
        );
    }

    listLeadAssignments(options: { assigneeId?: string | null } = {}): Observable<LeadAssignmentRecord[]> {
        let params = new HttpParams();
        const assigneeId = String(options.assigneeId || '').trim();
        if (assigneeId) params = params.set('assignee_id', assigneeId);
        return this.api.get<any>('/API2/lead-assignments', params).pipe(
            map((resp) => Array.isArray(resp?.data) ? resp.data : []),
            map((rows: any[]): LeadAssignmentRecord[] => rows.map((row): LeadAssignmentRecord => ({
                id: String(row?.id || ''),
                scopeType: String(row?.scope_type ?? row?.scopeType ?? '') === 'project'
                    ? 'project'
                    : String(row?.scope_type ?? row?.scopeType ?? '') === 'specific'
                        ? 'specific'
                        : 'all',
                projectId: row?.project_id ?? row?.projectId ?? null,
                projectName: row?.project_name ?? row?.projectName ?? null,
                leadId: row?.lead_id ?? row?.leadId ?? null,
                leadName: row?.lead_name ?? row?.leadName ?? null,
                assigneeType: String(row?.assignee_type ?? row?.assigneeType ?? '') === 'subpartner' ? 'subpartner' : 'sales',
                assigneeId: String(row?.assignee_id ?? row?.assigneeId ?? ''),
                assigneeName: String(row?.assignee_name ?? row?.assigneeName ?? '').trim() || 'Unnamed user',
                leadCount: Number(row?.lead_count ?? row?.leadCount ?? 0) || 0,
                createdAt: row?.created_at ?? row?.createdAt ?? null
            })).filter((row) => Boolean(row.id)))
        );
    }

    listAssignableLeads(options: { search?: string; page?: number; perPage?: number } = {}): Observable<{
        rows: LeadAssignmentLead[];
        page: number;
        perPage: number;
        total: number;
        totalPages: number;
    }> {
        const page = Math.max(1, Number(options.page) || 1);
        const perPage = Math.min(100, Math.max(1, Number(options.perPage) || 50));
        let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
        const search = String(options.search || '').trim();
        if (search) params = params.set('search', search);
        return this.api.get<any>('/API2/lead-assignments/leads', params).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : [];
                const meta = resp?.meta ?? {};
                return {
                    rows: rows.map((row: any): LeadAssignmentLead => ({
                        id: String(row?.id || ''),
                        name: String(row?.name || '').trim() || 'Unnamed lead',
                        email: row?.email ? String(row.email) : null,
                        phone: row?.phone ? String(row.phone) : null,
                        status: row?.status ? String(row.status) : null,
                        createdAt: row?.created_at ?? row?.createdAt ?? null,
                        propertyTitle: row?.property_title ?? row?.propertyTitle ?? null,
                        unitNumber: row?.unit_number ?? row?.unitNumber ?? null,
                        projectName: row?.project_name ?? row?.projectName ?? null
                    })).filter((row: LeadAssignmentLead) => Boolean(row.id)),
                    page: Number(meta?.page ?? page) || page,
                    perPage: Number(meta?.per_page ?? meta?.perPage ?? perPage) || perPage,
                    total: Number(meta?.total ?? rows.length) || 0,
                    totalPages: Number(meta?.total_pages ?? meta?.totalPages ?? 1) || 1
                };
            })
        );
    }

    assignLeads(payload: {
        assigneeId: string;
        assigneeType: LeadAssignmentAssigneeType;
        scopeType: LeadAssignmentScope;
        projectId?: string | null;
        leadIds?: string[];
    }): Observable<{ assignmentIds: string[]; affectedLeadCount: number }> {
        return this.api.post<any>('/API2/lead-assignments', {
            assignee_id: String(payload.assigneeId || '').trim(),
            assignee_type: payload.assigneeType,
            scope_type: payload.scopeType,
            project_id: payload.projectId ? String(payload.projectId).trim() : undefined,
            lead_ids: Array.isArray(payload.leadIds) ? payload.leadIds.map((id) => String(id || '').trim()).filter(Boolean) : undefined
        }).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((data: any) => ({
                assignmentIds: Array.isArray(data?.assignment_ids ?? data?.assignmentIds)
                    ? (data.assignment_ids ?? data.assignmentIds).map((id: any) => String(id || '')).filter(Boolean)
                    : [],
                affectedLeadCount: Number(data?.affected_lead_count ?? data?.affectedLeadCount ?? 0) || 0
            }))
        );
    }

    deleteLeadAssignment(assignmentId: string): Observable<void> {
        const id = encodeURIComponent(String(assignmentId || '').trim());
        if (!id) return of(void 0);
        return this.api.delete<any>(`/API2/lead-assignments/${id}`).pipe(map(() => void 0));
    }

    getLeadProfile(leadId: string): Observable<LeadProfileRecord> {
        const id = String(leadId || '').trim();
        if (!id) {
            return of({
                id: '',
                userId: null,
                propertyId: null,
                propertyTitle: null,
                propertyStatus: null,
                unitNumber: null,
                projectId: null,
                projectName: null,
                phaseId: null,
                phaseName: null,
                createdByUserId: null,
                createdByName: null,
                createdByEmail: null,
                createdByPhone: null,
                createdByRole: null,
                sourceLabel: null,
                notes: null,
                status: null,
                createdAt: null,
                updatedAt: null,
                holdExpiresAt: null,
                holdExtensionCount: null,
                customerName: null,
                email: null,
                phone: null,
                identityStatus: null,
                confirmedData: null,
                profilePictureFileId: null,
      idDocumentFileId: null,
      idDocumentName: null,
      idDocumentBackFileId: null,
      idDocumentBackName: null,
      reservationPdfFileId: null,
                zohoSign: null
            });
        }

        return this.api.get<any>(`/API2/leads/${encodeURIComponent(id)}`).pipe(
            map((resp) => resp?.data ?? resp ?? {}),
            map((row: any) => ({
                id: String(row?.ROWID ?? row?.id ?? ''),
                userId: row?.USER_ID ?? row?.user_id ?? null,
                propertyId: row?.PROPERTY_ID ?? row?.property_id ?? null,
                propertyTitle: row?.PROPERTY_TITLE ?? row?.property_title ?? null,
                propertyStatus: row?.PROPERTY_STATUS ?? row?.property_status ?? null,
                unitNumber: row?.UNIT_NUMBER ?? row?.unit_number ?? null,
                projectId: row?.PROJECT_ID ?? row?.project_id ?? null,
                projectName: row?.PROJECT_NAME ?? row?.project_name ?? null,
                phaseId: row?.PHASE_ID ?? row?.phase_id ?? null,
                phaseName: row?.PHASE_NAME ?? row?.phase_name ?? null,
                createdByUserId: row?.CREATED_BY_USER_ID ?? row?.created_by_user_id ?? null,
                createdByName: row?.CREATED_BY_NAME ?? row?.created_by_name ?? null,
                createdByEmail: row?.CREATED_BY_EMAIL ?? row?.created_by_email ?? null,
                createdByPhone: row?.CREATED_BY_PHONE ?? row?.created_by_phone ?? null,
                createdByRole: row?.CREATED_BY_ROLE ?? row?.created_by_role ?? null,
                sourceLabel: row?.SOURCE_LABEL ?? row?.source_label ?? null,
                notes: row?.NOTES ?? row?.notes ?? null,
                status: row?.STATUS ?? row?.status ?? null,
                createdAt: row?.CREATEDTIME ?? row?.created_at ?? null,
                updatedAt: row?.MODIFIEDTIME ?? row?.updated_at ?? null,
                holdExpiresAt: row?.HOLD_EXPIRES_AT ?? row?.hold_expires_at ?? null,
                holdExtensionCount: Number(row?.HOLD_EXTENSION_COUNT ?? row?.hold_extension_count ?? 0) || 0,
                customerName: row?.CUSTOMER_NAME ?? row?.customer_name ?? null,
                email: row?.EMAIL ?? row?.email ?? null,
                phone: row?.PHONE ?? row?.phone ?? null,
                identityStatus: row?.IDENTITY_STATUS ?? row?.identity_status ?? null,
                confirmedData: row?.CONFIRMED_DATA ?? row?.confirmed_data ?? null,
                profilePictureFileId: row?.PROFILE_PICTURE_FILE_ID ?? row?.profile_picture_file_id ?? null,
      idDocumentFileId: row?.ID_DOCUMENT_FILE_ID ?? row?.id_document_file_id ?? null,
      idDocumentName: row?.ID_DOCUMENT_NAME ?? row?.id_document_name ?? null,
      idDocumentBackFileId: row?.ID_DOCUMENT_BACK_FILE_ID ?? row?.id_document_back_file_id ?? null,
      idDocumentBackName: row?.ID_DOCUMENT_BACK_NAME ?? row?.id_document_back_name ?? null,
                reservationPdfFileId: row?.PDF_FILE_ID ?? row?.pdf_file_id ?? null,
                zohoSign: row?.ZOHO_SIGN ?? row?.zoho_sign ?? null
            }))
        );
    }

    getLeads(options: { page?: number; perPage?: number } = {}): Observable<ReservationRecord[]> {
        return this.getLeadsPage(options).pipe(map((result) => result.rows));
    }

    /**
     * One page of leads plus the server-reported total, so callers can walk every
     * page instead of silently rendering the first 25 rows.
     * `per_page` is capped at LEADS_PAGE_SIZE server-side (parsePagination in
     * reservations.routes.js) — asking for more returns fewer rows than requested
     * and used to stop paging loops early.
     */
    getLeadsPage(options: { page?: number; perPage?: number; search?: string; status?: string } = {}): Observable<{
        rows: ReservationRecord[];
        page: number;
        perPage: number;
        total: number;
        statusCounts: Record<string, number> | null;
    }> {
        const page = Math.max(1, options.page ?? 1);
        const perPage = Math.min(LEADS_PAGE_SIZE, Math.max(1, options.perPage ?? 25));
        let params = new HttpParams()
            .set('page', String(page))
            .set('per_page', String(perPage));
        const search = String(options.search || '').trim();
        if (search) params = params.set('search', search);
        // Server-side status filter. `all` is the client's "no filter" sentinel and
        // is deliberately not sent — the API treats an unrecognized status as no
        // filter, but sending it would be a lie about intent.
        const status = String(options.status || '').trim();
        if (status && status !== 'all') params = params.set('status', status);

        return this.api.get<any>(this.PATH, params).pipe(
            map((resp) => {
                const rows = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
                const totalRaw = Number(resp?.meta?.total ?? resp?.total ?? rows.length);
                const rawStatusCounts = resp?.meta?.status_counts ?? resp?.meta?.statusCounts ?? null;
                const statusCounts = rawStatusCounts && typeof rawStatusCounts === 'object'
                    ? Object.entries(rawStatusCounts).reduce<Record<string, number>>((result, [status, count]) => {
                        const parsed = Number(count);
                        result[status] = Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : 0;
                        return result;
                    }, {})
                    : null;
                return {
                    rows: rows.map((row: any) => this.mapLead(row)),
                    page,
                    perPage,
                    total: Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : rows.length,
                    statusCounts
                };
            })
        );
    }

    private mapLead(row: any): ReservationRecord {
        const currency = row?.currency || {};
        return {
            id: String(row?.id || row?.ROWID || row?.row_id || row?.rowId || ''),
            propertyLabel: row?.propertyLabel || row?.property || row?.property_id || '—',
            quotaNumber: row?.quotaNumber ?? row?.quota_number ?? null,
            customer: row?.customerName || row?.customer || '—',
            email: row?.customerEmail ?? row?.email ?? null,
            phone: row?.customerPhone ?? row?.phone ?? null,
            notes: row?.notes ?? null,
            amount: typeof row?.amount === 'number' ? row.amount : null,
            price: typeof row?.price === 'number' ? row.price : null,
            installmentAmount: typeof row?.installmentAmount === 'number' ? row.installmentAmount : null,
            status: row?.status || 'pending',
            reservationFormUrl: row?.reservationFormUrl ?? row?.reservation_form_url ?? null,
            propertyId: row?.propertyId ?? row?.property_id ?? null,
            createdTime: row?.createdTime ?? row?.CREATEDTIME ?? row?.created_time ?? null,
            currency: {
                code: currency.code ?? currency.CODE ?? null,
                symbol: currency.symbol ?? currency.SYMBOL ?? null
            }
        };
    }
}
